import { Component, OnInit, Input, ComponentFactoryResolver } from '@angular/core';
import {
	AbstractModal,
	BroadcastEventModel,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType,
	ModalService,
	ModalSize,
} from '@cv-invision/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { AddEditGenericIdentifierComponent } from '../generic-add-edit/add-edit-generic-identifier.component';

@Component({
	selector: 'cv-generic-identifier-list',
	templateUrl: './generic-identifier-list.component.html',
	styleUrls: ['./generic-identifier-list.component.scss'],
	providers: [ConfirmationService, MessageService],
})
export class GenericIdentifierListComponent extends AbstractModal implements OnInit {
	@Input() listTitle: string;
	@Input() labelAddButton: string;
	@Input() identifierLabel: string;
	@Input() secondFieldLabel: string;
	@Input() showSecondField = true;
  @Input() showSecondFieldOnCreate = false;
  @Input() secondFieldName = null;
	@Input() disableSecondFieldInEdit = false;

	@Input() service: any;
	@Input() broadcastEvent: string;
	@Input() getListFn: (integrationName: string) => any;
	@Input() deleteFn: (integrationName: string, item: any) => any;
	@Input() getSecondFieldValue: (model: any) => any;
	@Input() modelFactory: () => any;
	@Input() integrationName: string;

	public items: any[] = [];
	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private broadcastService: BroadcastService,
		private confirmationService: ConfirmationService,
		private messageService: MessageService
	) {
		super();
	}

	ngOnInit(): void {
		this.buttons.push({
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this),
		});

		this.loadData();

		this.broadcastService.on(this.broadcastEvent, (broadcast: BroadcastEventModel) => {
			this.loadData();
			this.messageService.add({
				severity: 'success',
				summary: 'Success',
				detail: `${this.listTitle} was ${broadcast.event.isEditing ? 'updated' : 'added'}`,
			});
		});
	}

	loadData(): void {
		this.getListFn(this.integrationName)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result: any[]) => (this.items = result));
	}

	addEditItem(item: any = null): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: AddEditGenericIdentifierComponent,
				data: {
					model: item || this.modelFactory(),
					integrationName: this.integrationName,
					isEditing: !!item,
					saveFn: item ? this.service.update.bind(this.service) : this.service.create.bind(this.service),
					broadcastEvent: this.broadcastEvent,
					labels: {
						identifierLabel: this.identifierLabel,
						secondFieldLabel: this.secondFieldLabel,
					},
					showSecondField: this.showSecondField,
					disableSecondFieldInEdit: this.disableSecondFieldInEdit,
					getSecondFieldValue: this.getSecondFieldValue,
          showSecondFieldOnCreate: this.showSecondFieldOnCreate,
          secondFieldName: this.secondFieldName,
				},
				modalConfiguration: {
					title: `${item ? 'Edit' : 'Add'} ${this.listTitle}`,
					modalSize: ModalSize.XL,
				},
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe();
	}

	onShowDeleteConfirm(item: any): void {
		this.confirmationService.confirm({
			message: 'Are you sure that you want to proceed?',
			header: 'Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => this.deleteItem(item),
		});
	}

	deleteItem(item: any): void {
		this.deleteFn(this.integrationName, item)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(() => {
				this.loadData();
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: `${this.listTitle} was deleted`,
				});
			});
	}

	onDismiss(): void {
		super.onCancel();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.complete();
	}
}
